<template>
    <div class="catalogue-list p-3">
        <div class="header-card d-flex justify-content-between align-items-center mb-4 p-3 rounded shadow-sm">
            <div>
                <h1 v-if="tenant_id">{{ userStore.tenant.name }}</h1>
                <h2 class="mb-1">Liste des catalogues</h2>
                <small class="text-muted">Total : {{ catalogues.length }} catalogues</small>
            </div>
            <div v-if="!tenant_id">
                <button class="btn btn-secondary m-1" @click="goToQRPage">
                    <i class="bi bi-qr-code me-1"></i>QR Code
                </button>
                <button class="btn btn-primary m-1" @click="$router.push({ name: 'catalogues.create' })">
                    <i class="bi bi-plus-circle me-1"></i>Ajouter
                </button>
            </div>
        </div>

        <div class="row mb-4">
            <div class="col-md-6 col-12">
                <input type="text" class="form-control" placeholder="Recherche par label, code ou description"
                    v-model="search" />
            </div>
        </div>

        <div v-if="cataloguesToPrint.length > 0">
            <div class="d-none d-md-block table-responsive">
                <table class="table table-hover table-striped align-middle catalogue-table">
                    <thead class="table-purple text-white">
                        <tr>
                            <th>#</th>
                            <th>Label</th>
                            <th>Code</th>
                            <th>Image</th>
                            <th>Description</th>
                            <th v-if="!tenant_id">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(catalogue, idx) in cataloguesToPrint" :key="catalogue.id" class="table-row-hover">
                            <td>{{ idx + 1 }}</td>
                            <td>{{ catalogue.label || '—' }}</td>
                            <td>{{ catalogue.code || '—' }}</td>
                            <td>
                                <img :src="$api.defaults.assetsBaseURL + catalogue.image_path" alt="Catalogue image"
                                    class="img-fluid rounded catalogue-image cursor-pointer"
                                    @click="openImageModal(catalogue.image_path)" />
                            </td>
                            <td style="white-space: pre-wrap;">{{ catalogue.description || 'Aucune description' }}</td>
                            <td v-if="!tenant_id">
                                <button class="btn btn-sm btn-secondary me-1"
                                    @click="$router.push({ name: 'catalogues.edit', params: { id: catalogue.id } })">Modifier</button>
                                <button class="btn btn-sm btn-danger"
                                    @click="deleteCatalogue(catalogue.id)">Supprimer</button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div class="d-block d-md-none">
                <div class="card mb-2" v-for="catalogue in cataloguesToPrint" :key="catalogue.id">
                    <div class="card-body">
                        <div class="row g-3 align-items-center">
                            <div class="col-5">
                                <img :src="$api.defaults.assetsBaseURL + catalogue.image_path" alt="Catalogue image"
                                    class="img-fluid rounded catalogue-image cursor-pointer"
                                    @click="openImageModal(catalogue.image_path)" />
                            </div>
                            <div class="col-7">
                                <h5 class="mb-1">{{ catalogue.label }}</h5>
                                <p class="mb-1"><strong>Code :</strong> {{ catalogue.code }}</p>
                                <p class="mb-1 text-muted small" style="white-space: pre-wrap;">{{ catalogue.description
                                    }}</p>
                            </div>
                        </div>
                        <div v-if="!tenant_id" class="d-flex gap-2 mt-2">
                            <button class="btn btn-sm btn-secondary flex-fill"
                                @click="$router.push({ name: 'catalogues.edit', params: { id: catalogue.id } })">Modifier</button>
                            <button class="btn btn-sm btn-danger flex-fill"
                                @click="deleteCatalogue(catalogue.id)">Supprimer</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-else class="alert alert-info rounded">Aucun catalogue trouvé. Essayez une autre recherche.</div>
    </div>

    <!-- Image Modal -->
    <div v-if="showModal" class="modal fade show d-block" @click="closeImageModal"
        style="background-color: rgba(0,0,0,0.8);">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content border-0 bg-transparent">
                <div class="modal-body text-center p-0">
                    <img :src="$api.defaults.assetsBaseURL + selectedImage" class="img-fluid rounded" @click.stop />
                    <button type="button" class="btn-close btn-close-white position-absolute top-0 end-0 m-3"
                        @click="closeImageModal"></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import { useUserStore } from '@/store/userStore';

export default {
    name: 'CatalogueList',
    props: {
        tenant_id: {
            type: [String, Number],
            required: false
        }
    },

    data() {
        return {
            search: '',
            catalogues: [],
            link: '/catalogs',
            showModal: false,
            selectedImage: null
        };
    },
    computed: {
        cataloguesToPrint() {
            const query = this.search.trim().toLowerCase();
            if (query.length < 2) {
                return this.catalogues;
            }
            return this.catalogues.filter(catalogue => {
                return (catalogue.label?.toLowerCase().includes(query) || false)
                    || (catalogue.code?.toLowerCase().includes(query) || false);
                // || (catalogue.description?.toLowerCase().includes(query) || false);
            });
        },
        userStore() {
            return useUserStore();
        },
    },
    methods: {
        getCatalogues() {
            if (this.tenant_id) {
                this.link = `/catalogs_for_clients/${this.tenant_id}`;
            }
            this.$api.get(this.link)
                .then(res => {
                    this.catalogues = res.data.data || [];
                })
                .catch(err => {
                    console.error("Error fetching catalogues:", err);
                    Swal.fire('Erreur', 'Une erreur est survenue lors du chargement des catalogues.', 'error');
                });
        },
        openImageModal(image) {
            this.selectedImage = image;
            this.showModal = true;
        },
        closeImageModal() {
            this.showModal = false;
            this.selectedImage = null;
        },
        deleteCatalogue(id) {
            this.$swal.fire({
                title: 'Êtes-vous sûr?',
                text: "Vous ne pourrez pas revenir en arrière!",
                icon: 'warning',
                showDenyButton: true,
                confirmButtonText: "Supprimer",
                confirmButtonColor: 'red',
                denyButtonColor: 'green',
                denyButtonText: 'Garder'
            })
                .then((result) => {
                    if (result.isConfirmed) {
                        this.$api.delete(`${this.link}/${id}`)
                            .then(() => {
                                this.catalogues = this.catalogues.filter(c => c.id !== id);
                                this.$toast.fire({
                                    title:'Succès', 
                                    text:'Catalogue supprimé avec succès.', 
                                    icon:'success'});
                            })
                            .catch(err => {
                                console.error('Error deleting catalogue:', err);
                                this.$toast.fire({
                                    title:'Erreur', 
                                    text:'Une erreur est survenue lors de la suppression du catalogue.', 
                                    icon:'error'});
                            });
                    }
                    else if (result.isDenied || result.isDismissed) {
                        this.$toast.fire({
                            text: 'Suppression annulée',
                            icon: 'info',
                        })
                    }

                })
        },
        goToQRPage() {
            this.$router.push({ name: 'catalogues.qr' });
        }
    },
    mounted() {
        this.getCatalogues();
    }
};
</script>

<style scoped>
.catalogue-list {
    background: linear-gradient(130deg, #f8f2ff 0%, #fff 60%);
}

.header-card {
    background-color: rgba(106, 74, 166, 0.12);
    border: 1px solid rgba(106, 74, 166, 0.3);
}

.table-purple {
    background-color: #6a4aa6;
    border-color: #6a4aa6;
}

.catalogue-table {
    border: 1px solid rgba(106, 74, 166, 0.3);
    box-shadow: 0 6px 18px rgba(106, 74, 166, 0.12);
}

.catalogue-image {
    max-height: 120px;
    object-fit: cover;
    width: 100%;
}

.cursor-pointer {
    cursor: pointer;
}

.mobile-catalogue-card {
    border: 1px solid rgba(106, 74, 166, 0.18);
    border-radius: 12px;
    padding: 1rem;
    background: #ffffff;
    box-shadow: 0 8px 24px rgba(106, 74, 166, 0.08);
}

.mobile-catalogue-card h5 {
    margin-bottom: 0.4rem;
}
</style>
